/** API functions for chat REST endpoints. */

import axios from 'axios';
import { apiClient } from '@/lib/api-client';
import type {
  ChatMessage,
  ChatMessageMetadata,
  ChatThreadsBatchResponse,
  Conversation,
  TriageQuestion,
  TriageResult,
} from '@/types/chat';

const BASE = '/communications/chat/conversations';

export const getChatAvailability = async () => {
  const { data } = await apiClient.get(`${BASE}/availability`);
  return data;
};

export const getActiveConversation = async (): Promise<Conversation> => {
  const { data } = await apiClient.get(`${BASE}/active`);
  return data;
};

export const getConversationForUser = async (userId: string): Promise<Conversation> => {
  const { data } = await apiClient.get(`${BASE}/for-user/${userId}`);
  return data;
};

const INBOX_FETCH_CONCURRENCY = 4;

let chatThreadsBatchSupported: boolean | null = null;

const isBatchEndpointUnavailable = (error: unknown): boolean =>
  axios.isAxiosError(error) && [404, 405, 501].includes(error.response?.status ?? 0);

const conversationsFromBatchResponse = (
  data: ChatThreadsBatchResponse
): Map<string, Conversation> =>
  new Map(
    Object.entries(data).filter((entry): entry is [string, Conversation] => entry[1] != null)
  );

/** Staff inbox batch endpoint — one request for many member threads. */
export const getChatThreadsBatch = async (
  userIds: string[]
): Promise<Map<string, Conversation>> => {
  if (userIds.length === 0) return new Map();

  const { data } = await apiClient.get<ChatThreadsBatchResponse>(`${BASE}/threads`, {
    params: { user_ids: userIds.join(',') },
  });

  return conversationsFromBatchResponse(data);
};

const getConversationsForUsersSequential = async (
  userIds: string[]
): Promise<Map<string, Conversation>> => {
  const map = new Map<string, Conversation>();

  for (let i = 0; i < userIds.length; i += INBOX_FETCH_CONCURRENCY) {
    const chunk = userIds.slice(i, i + INBOX_FETCH_CONCURRENCY);
    const entries = await Promise.all(
      chunk.map(async (userId) => {
        try {
          const conversation = await getConversationForUser(userId);
          return [userId, conversation] as const;
        } catch {
          return [userId, null] as const;
        }
      })
    );

    for (const [userId, conversation] of entries) {
      if (conversation) map.set(userId, conversation);
    }
  }

  return map;
};

/** Staff inbox: batch-fetch conversations when supported, else capped parallel fallback. */
export const getConversationsForUsers = async (
  userIds: string[]
): Promise<Map<string, Conversation>> => {
  if (userIds.length === 0) return new Map();

  if (chatThreadsBatchSupported !== false) {
    try {
      const batch = await getChatThreadsBatch(userIds);
      chatThreadsBatchSupported = true;
      return batch;
    } catch (error) {
      if (isBatchEndpointUnavailable(error)) {
        chatThreadsBatchSupported = false;
      } else {
        throw error;
      }
    }
  }

  return getConversationsForUsersSequential(userIds);
};

export const getConversationMessages = async (
  conversationId: string,
  page = 1
): Promise<{ results: ChatMessage[]; count: number }> => {
  const { data } = await apiClient.get(`${BASE}/${conversationId}/messages`, {
    params: { page },
  });
  return data;
};

/** Send a message and get bot responses in the same HTTP response. */
export const sendChatMessage = async (payload: ChatSendPayload): Promise<ChatSendResponse> => {
  const { data } = await apiClient.post(`${BASE}/send`, payload);
  return data;
};

/** Staff: send a message for a specific client. */
export const sendChatMessageForUser = async (
  userId: string,
  payload: ChatSendPayload
): Promise<ChatSendResponse> => {
  const { data } = await apiClient.post(`${BASE}/send-for-user/${userId}`, payload);
  return data;
};

/** Build metadata for a user's stored AI context. */
export interface UserContextMeta {
  context_version: number;
  last_built_at: string | null;
  s3_key: string;
  build_status: string;
  build_error: string;
  token_count: number;
  needs_rebuild?: boolean;
}

/** One domain section of the aggregated context. */
export interface UserContextDomain {
  domain: string;
  priority?: number;
  data?: Record<string, unknown>;
  token_count?: number;
}

export interface UserContextResponse {
  user_id: string;
  user_profile_id?: string;
  /** Which layer served the payload: `s3`, `pg_fallback`, or `chat_read_path`. */
  source: string;
  /** True when the LLM token budget was applied to the returned context. */
  truncated: boolean;
  context: Record<string, unknown>;
  raw_jsonl?: string | null;
  meta: UserContextMeta | null;
}

/** Staff: manually rebuild AI context for a user (synchronous). */
export const rebuildUserContext = async (
  userId: string
): Promise<UserContextResponse & { detail: string; s3_key: string }> => {
  const { data } = await apiClient.post(`${BASE}/rebuild-context/${userId}`);
  return data;
};

/**
 * Staff: get current AI context for a user.
 *
 * `full` returns every domain untruncated plus the raw JSONL stored on S3;
 * without it the response mirrors the budgeted view the chat model sees.
 */
export const getUserContext = async (
  userId: string,
  options: { full?: boolean; source?: 's3' } = {}
): Promise<UserContextResponse> => {
  const params: Record<string, string> = {};
  if (options.full) params.full = 'true';
  if (options.source) params.source = options.source;
  const { data } = await apiClient.get(`${BASE}/user-context/${userId}`, { params });
  return data;
};

export interface BulkContextRebuildResponse {
  detail: string;
  matched: number;
  mode?: 'sync' | 'async';
  would_rebuild?: number;
  queued?: number;
  attempted?: number;
  succeeded?: number;
  failed?: number;
  failures?: Array<{ user_profile_id: string; error: string }>;
  remaining?: number;
}

/** Staff: rebuild AI context on S3 for every active client. */
export const rebuildAllUserContexts = async (
  payload: {
    limit?: number;
    dry_run?: boolean;
    async_mode?: boolean;
    stale_only?: boolean;
  } = {}
): Promise<BulkContextRebuildResponse> => {
  const { data } = await apiClient.post('/communications/chat/context/rebuild-all', payload);
  return data;
};

/** A prior turn sent back to the assistant so it can follow multi-step flows. */
export type PlatformChatHistoryTurn = { role: 'user' | 'bot'; content: string };

export type PlatformChatSendOptions = {
  mentionedUserId?: string | null;
  /** Bedrock model id from the clinician picker; omit to use the server default. */
  modelId?: string | null;
};

/** Platform chat — staff-only chatbot for querying backoffice data. */
export const sendPlatformChatMessage = async (
  text: string,
  history: PlatformChatHistoryTurn[] = [],
  options: PlatformChatSendOptions = {}
): Promise<{ response: { type: string; text: string; metadata: Record<string, unknown> } }> => {
  const { data } = await apiClient.post('/communications/platform-chat/send', {
    text,
    history,
    ...(options.mentionedUserId ? { mentioned_user_id: options.mentionedUserId } : {}),
    ...(options.modelId ? { model_id: options.modelId } : {}),
  });
  return data;
};

export type ChatSendPayload =
  | { type: 'message'; text: string }
  | { type: 'chip'; action: string }
  | { type: 'triage_answer'; step: number; answer: string }
  | { type: 'voice'; audio_url: string };

/** Get a presigned upload URL for chat audio recordings. */
export const getAudioUploadUrl = async (
  fileName: string
): Promise<{ upload_url: string; file_id: string; key: string }> => {
  const { data } = await apiClient.post('storage/files/presigned-upload', {
    file_name: fileName,
    content_type: 'audio/webm',
    folder: 'chat-audio',
  });
  return data;
};

export interface ChatSendResponse {
  responses: ChatBotEvent[];
}

export type ChatBotEvent =
  | { type: 'bot_message'; text: string; message_type?: string; metadata: ChatMessageMetadata }
  | { type: 'emergency'; ticket_number: string }
  | { type: 'triage_question'; step: number; question: TriageQuestion }
  | { type: 'triage_outcome'; result: TriageResult }
  | { type: 'availability'; is_open: boolean; next_open: string | null }
  | { type: 'error'; message: string };
